import { getLocale, consoleEvents } from '@pb/utils'
import { juxt, curry } from 'ramda'

import { ACCOUNT_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { removeQuery } from 'helpers/removeQueryParameters'
import runKlarnaOperation from 'helpers/runKlarnaOperation'
import { getFullTouchpointUrl } from 'helpers/touchpointUrl'

import { SELECTED_INVOICE_PARAM, ALL_TAB } from '../constants'
import {
  fetchOmniCreditOrders,
  fetchOmniCreditOrder,
  fetchOmniCreditProfile,
  fetchOmniCreditInvoice,
  fetchOmniCreditMonthlyInvoiceOrders,
  fetchOmniCreditSubscriptionCollections,
} from '../requests'
import {
  fetchOrdersStart,
  fetchOrdersSuccess,
  fetchOrdersFail,
  fetchOrderStart,
  fetchOrderSuccess,
  fetchOrderFail,
  loadMoreOrdersStart,
  loadMoreOrdersSuccess,
  loadMoreOrdersFail,
  setIsLoadMoreUsed,
  toggleExpandedPath,
  setKlarnaAuthStart,
  fetchKlarnaProfileStart,
  fetchKlarnaProfileSuccess,
  fetchMonthlyInvoicesStart,
  fetchMonthlyInvoicesSuccess,
  fetchMonthlyInvoicesFail,
  fetchMonthlyInvoiceOrdersStart,
  fetchMonthlyInvoiceOrdersSuccess,
  fetchMonthlyInvoiceOrdersFail,
  fetchMonthlyInvoiceStart,
  fetchMonthlyInvoiceSuccess,
  fetchMonthlyInvoiceFail,
  setSelectedTab,
} from './actions'
import {
  ORDER_LIST_INITIAL_SIZE,
  MONTHLY_ORDER_LIST_INITIAL_SIZE,
  TODO_ORDER_LIST_INITIAL_SIZE,
  LOAD_MORE_ORDERS_PAGE_SIZE,
  MONTHLY_INVOICE_LIST_INITIAL_SIZE,
  LOAD_MORE_MONTHLY_INVOICES_PAGE_SIZE,
  FILTER_NAME,
  OPERATION_RESULTS,
} from './constants'
import {
  normalizeKlarnaOrders,
  normalizeKlarnaOrder,
  normalizeKlarnaProfile,
  normalizeKlarnaMonthlyInvoices,
  normalizeKlarnaMonthlyInvoice,
} from './normalize'
import selectors from './selectors'

// ----------------------------------------
// FETCH ORDERS
// ----------------------------------------
export const operationFetchOrders =
  (filterName = FILTER_NAME.ALL) =>
  (dispatch, getState) => {
    const state = getState()
    dispatch(fetchOrdersStart(filterName))

    const offset = selectors.getNextOffsetForOrderFilter(filterName, state)
    const initialListSize =
      filterName === FILTER_NAME.TO_DO ? TODO_ORDER_LIST_INITIAL_SIZE : ORDER_LIST_INITIAL_SIZE
    const size = offset === 0 ? initialListSize : LOAD_MORE_ORDERS_PAGE_SIZE

    return fetchOmniCreditOrders(size, offset, filterName)
      .then((result) => {
        const normalizedResult = normalizeKlarnaOrders(result)
        dispatch(fetchOrdersSuccess(normalizedResult, filterName))
      })
      .catch((err) => dispatch(fetchOrdersFail(err, filterName)))
  }

export const operationFetchOmnicreditOrderDetails = (orderId: string) => async (dispatch) => {
  dispatch(fetchOrderStart(orderId))
  fetchOmniCreditOrder(orderId)
    .then((result) => {
      const normalizedOrder = normalizeKlarnaOrder(result)
      dispatch(fetchOrderSuccess(normalizedOrder))
    })
    .catch((error) => dispatch(fetchOrderFail(error)))
}

export const operationLoadMoreOrders =
  (filterName = FILTER_NAME.ALL) =>
  (dispatch, getState) => {
    const state = getState()
    dispatch(loadMoreOrdersStart(filterName))

    const offset = selectors.getNextOffsetForOrderFilter(filterName, state)

    dispatch(setIsLoadMoreUsed(true))

    fetchOmniCreditOrders(LOAD_MORE_ORDERS_PAGE_SIZE, offset, filterName)
      .then((result) => {
        const normalizedResult = normalizeKlarnaOrders(result)
        dispatch(loadMoreOrdersSuccess(normalizedResult, filterName))

        setTimeout(() => {
          dispatch(setIsLoadMoreUsed(false))
        }, LOAD_MORE_ORDERS_PAGE_SIZE * 300)
      })
      .catch((err) => dispatch(loadMoreOrdersFail(err, filterName)))
  }

export const operationFetchKlarnaProfile = () => (dispatch) => {
  dispatch(fetchKlarnaProfileStart())

  return fetchOmniCreditProfile()
    .then((result) => {
      const normalizedResult = normalizeKlarnaProfile(result)
      dispatch(fetchKlarnaProfileSuccess(normalizedResult))
    })
    .catch((error) => {
      consoleEvents.error(error)
    })
}

export const operationRunKlarnaManageMonthlyOperation = (token: string) => (dispatch) => {
  runKlarnaOperation(token)
    .then(({ status } = {}) => {
      if (status !== OPERATION_RESULTS.NO_STATE_CHANGE) {
        // Fetch new Klarna data since operation has resulted in a change of the state
        dispatch(operationFetchKlarnaProfile())
      }
    })
    .catch(({ status } = {}) => {
      if (status === OPERATION_RESULTS.TOKEN_EXPIRED) {
        // Reload the page since that will result in the token being refreshed
        window.location.reload()
      }
    })
}

export const operationExpandPath = (pathWithFilter, id) => (dispatch) =>
  dispatch(toggleExpandedPath(pathWithFilter, id))

export const operationExpandMonthlyInvoiceForFilter = curry((filter, id) =>
  operationExpandPath(['monthlyInvoices', 'filters', filter, 'expandedMonthlyInvoiceId'], id),
)

export const operationExpandMonthlyInvoiceOrderForFilter = curry((filter, id) =>
  operationExpandPath(['monthlyInvoices', 'filters', filter, 'expandedOrderId'], id),
)

export const operationExpandOrderForFilter = curry((filter, id) =>
  operationExpandPath(['orders', 'filters', filter, 'expandedOrderId'], id),
)

// operationSetExpandedIds :: set all expanded id's for filter to new id ( order, monhtly invoice, monthly invoice order )
export const operationSetExpandedIdsForFilter = curry(
  (filter, id) => (state) =>
    juxt([
      operationExpandMonthlyInvoiceOrderForFilter(filter, id),
      operationExpandMonthlyInvoiceForFilter(filter, id),
      operationExpandOrderForFilter(filter, id),
    ])(state),
)

export const operationRunKlarnaOrderOperation = (token: string, orderId: string) => (dispatch) => {
  runKlarnaOperation(token, orderId)
    .then(({ status } = {}) => {
      if (status !== OPERATION_RESULTS.NO_STATE_CHANGE) {
        // Fetch new Klarna data since operation has resulted in a change of the state
        dispatch(fetchOrderStart(orderId))
        fetchOmniCreditOrder(orderId)
          .then((result) => {
            const normalizedOrder = normalizeKlarnaOrder(result)
            dispatch(fetchOrderSuccess(normalizedOrder))
            dispatch(operationSetExpandedIdsForFilter(FILTER_NAME.TO_DO, ''))
            dispatch(operationSetExpandedIdsForFilter(FILTER_NAME.ALL, ''))
          })
          .catch((error) => dispatch(fetchOrderFail(error)))
      }
    })
    .catch(({ status } = {}) => {
      if (status === OPERATION_RESULTS.TOKEN_EXPIRED) {
        // Reload the page since that will result in the token being refreshed
        window.location.reload()
      }
    })
}

export const operationRunKlarnaMonthlyInvoiceOperation =
  (token: string, invoiceId: string) => (dispatch, getState) => {
    runKlarnaOperation(token, invoiceId)
      .then(({ status } = {}) => {
        if (status !== OPERATION_RESULTS.NO_STATE_CHANGE) {
          const state = getState()
          const subscriptionId = selectors.getMonthlySubscriptionId(state)
          dispatch(fetchMonthlyInvoiceStart(invoiceId))
          fetchOmniCreditInvoice(subscriptionId, invoiceId)
            .then((result) => {
              const normalizedInvoice = normalizeKlarnaMonthlyInvoice(result)
              dispatch(fetchMonthlyInvoiceSuccess(normalizedInvoice))
            })
            .catch((error) => dispatch(fetchMonthlyInvoiceFail(error)))
        }
      })
      .catch(({ status } = {}) => {
        if (status === OPERATION_RESULTS.TOKEN_EXPIRED) {
          // Reload the page since that will result in the token being refreshed
          window.location.reload()
        }
      })
  }

export const operationKlarnaAuth = () => (dispatch, getState) => {
  const state = getState()
  const klarnaClientId = appSelectors.getKlarnaClientId(state)
  const userEmail = appSelectors.getUserEmail(state)

  // Klarna wants format en-GB and not en_gb
  const splitLocale = getLocale().split('_')
  const klarnaLocale = `${splitLocale[0]}-${splitLocale[1].toUpperCase()}`

  dispatch(setKlarnaAuthStart())

  Klarna.PostPurchaseExperience.authorizationRequest({
    client_id: klarnaClientId,
    scope: 'read:profile_email read:consumer_order',
    redirect_uri: `${getFullTouchpointUrl()}/postpurchase/process-auth-response`,
    // State value is populated in the returned redirect URL as a "state" query parameter
    // We're using it to redirect to the specified path on authorization success
    state: ACCOUNT_ROUTES.HMPay.path,
    locale: klarnaLocale,
    response_type: 'code',
    login_hint: userEmail,
  })
}

export const operationFetchMonthlyInvoices = () => (dispatch, getState) => {
  const state = getState()
  dispatch(fetchMonthlyInvoicesStart())

  const subscriptionId = selectors.getMonthlySubscriptionId(state)
  const offset = selectors.getMonthlyInvoicesNextOffset(state)
  const size =
    offset === 0 ? MONTHLY_INVOICE_LIST_INITIAL_SIZE : LOAD_MORE_MONTHLY_INVOICES_PAGE_SIZE

  return fetchOmniCreditSubscriptionCollections(size, offset, subscriptionId)
    .then((result) => {
      const normalizedResult = normalizeKlarnaMonthlyInvoices(result)
      dispatch(fetchMonthlyInvoicesSuccess(normalizedResult))
    })
    .catch((err) => dispatch(fetchMonthlyInvoicesFail(err)))
}

export const operationFetchMonthlyInvoiceOrders = (collectionId) => (dispatch, getState) => {
  const state = getState()
  dispatch(fetchMonthlyInvoiceOrdersStart(collectionId))

  const subscriptionId = selectors.getMonthlySubscriptionId(state)

  const offset = selectors.getMonthlyInvoiceNextOffset(collectionId, state)
  const size = offset === 0 ? MONTHLY_ORDER_LIST_INITIAL_SIZE : LOAD_MORE_ORDERS_PAGE_SIZE

  return fetchOmniCreditMonthlyInvoiceOrders(subscriptionId, collectionId, size, offset)
    .then((result) => {
      const normalizedResult = normalizeKlarnaOrders(result)
      dispatch(fetchMonthlyInvoiceOrdersSuccess(collectionId, normalizedResult))
    })
    .catch((err) => dispatch(fetchMonthlyInvoiceOrdersFail(err)))
}

export const operationSetSelectedTab = (tab) => (dispatch) => dispatch(setSelectedTab(tab))

// operationExpandInvoiceWithQuery :: set all the expand ids and remove query param
export const operationExpandInvoiceWithQuery = (id, filter) => (dispatch, getState) => {
  const state = getState()

  if (selectors.getIsInvoiceInFilter(id, filter, state)) {
    // ?selectedInvoiceId=<Klarna Order ID> or ?selectedInvoiceId=<Klarna Monthly Collection ID>
    removeQuery(SELECTED_INVOICE_PARAM)
    dispatch(operationSetExpandedIdsForFilter(filter, id))
  } else {
    const orderIdBasedOnHmOrderNumber = selectors.getOrderIdByHmOrderNumber(id, state)
    if (selectors.getIsInvoiceInFilter(orderIdBasedOnHmOrderNumber, filter, state)) {
      // ?selectedInvoiceId=<H&M order number>
      removeQuery(SELECTED_INVOICE_PARAM)
      dispatch(operationSetExpandedIdsForFilter(filter, orderIdBasedOnHmOrderNumber))
    } else {
      // Open All invoices tab if no match found
      dispatch(operationSetSelectedTab(ALL_TAB.id))
    }
  }
}
